import { assertNever } from '@ofmos/shared/exhaustive';
import { isAnnotationAction } from '@ofmos/shared/record-write';
import { type ReactNode, useId, useState } from 'react';
import type { FieldMetadata, RecordEvent, RecordEventType } from '../../data/client';
import { fieldShape } from '../../fields/fieldTypeMeta';
import { formatDateTime, formatRelative } from '../../fields/format';
import { cx, Icon, type IconName } from '../../ui';
import { MOTION } from '../../ui/motion';
import { actorParts, actorSentence } from './ActorLabel';
import { type DiffMode, FieldDiffList } from './FieldDiffList';
import { fileDiffSentence } from './fileDiffSentence';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';

/**
 * ONE ENTRY IS ONE LIST ROW (wave 4, C2-12; `spec/screens/record.md` section 2 item 5).
 *
 * Round 1 rebuilt this part's SURFACES and left its construction alone, so the owner's second
 * click-through found the same thing he had found the first time: "Dieser Aspekt generell wirkt
 * ALT" (owner-86), "Sieht weird aus hier" (owner-88). What the pictures show is a three-line
 * event-log block - a category word as the title ("Note added", "Updated"), an avatar row under
 * it with the writer's name, then the body or a labelled field diff - which is a web activity
 * feed and not a row.
 *
 * The row is the spec's: a glyph in the kit's 24 slot, the title at 15, one secondary line at
 * 13, the time at the trailing edge at 13 in the secondary label colour, a hairline between
 * rows. Apple ships no timeline, so the two nearest constructions are still what this is made
 * of - the notification row's information order inside the kit's list-row geometry
 * (`components/MAP.md`, "Timeline").
 * kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Rows/Default
 *
 * AND THE TITLE CARRIES THE CONTENT, NOT THE CATEGORY. "Note added" and "Updated" are labels
 * for a kind of event; what a reader is scanning for is what the note SAYS and which value
 * changed, so a note row's title is the note's first line and a change row's title is the
 * sentence a reader was assembling out of the old label, the field name and the value anyway -
 * "Name changed to Larkwood Studio". The kind is left to the leading symbol, which is what a
 * list row's leading symbol is for.
 * hig: https://developer.apple.com/design/human-interface-guidelines/writing
 */
type EntryTone = 'neutral' | 'warning' | 'danger';

/**
 * WAVE 3 (R-B1): A TONE IS A COLOUR, NOT A FRAME.
 *
 * A refusal and a queued proposal used to draw a tinted, bordered box around their body,
 * inside a row that is itself inside the stream - the frame within a frame the HIG's layout
 * rule forbids and the third surface the owner counted on this screen. Apple marks a state
 * with the colour of the text and the symbol beside it: the HIG's colour page puts a
 * semantic colour in the CONTENT, and the kit ships no tinted note box for a list row.
 * hig: https://developer.apple.com/design/human-interface-guidelines/color
 */
const TONES: Record<EntryTone, string> = {
  neutral: 'text-ink',
  warning: 'text-warning',
  danger: 'text-danger',
};

interface EventPresentation {
  readonly icon: IconName;
  readonly tone: EntryTone;
  readonly diffMode: DiffMode;
}

function presentationOf(type: RecordEventType): EventPresentation {
  switch (type) {
    case 'created':
      return { icon: 'plus', tone: 'neutral', diffMode: 'change' };
    case 'updated':
      return { icon: 'pencil', tone: 'neutral', diffMode: 'change' };
    case 'deleted':
      return { icon: 'trash', tone: 'danger', diffMode: 'change' };
    case 'restored':
      return { icon: 'arrow-up', tone: 'neutral', diffMode: 'change' };
    // Erasure keeps the skeleton of the event and drops its values (erasure-and-audit.md),
    // which is why this entry is expected to have no diff to show.
    case 'erased':
      return { icon: 'trash', tone: 'danger', diffMode: 'change' };
    // A human handing one field back to the machines (INV-5). Quiet by design.
    case 'lock_released':
      return { icon: 'eye', tone: 'neutral', diffMode: 'change' };
    case 'write_blocked':
      return { icon: 'blocked', tone: 'danger', diffMode: 'refused' };
    case 'pending_approval':
      return { icon: 'clock', tone: 'warning', diffMode: 'proposed' };
    /**
     * INV-10: a proposal against this record that nobody decided in time. Warning, not
     * danger: nothing was lost and nothing was refused - a decision was simply never
     * taken, and the values shown are what the machine had wanted.
     */
    case 'proposal_expired':
      return { icon: 'clock', tone: 'warning', diffMode: 'proposed' };
    /**
     * identity-resolution.md 4.5. `merged_from` carries the diffs the merge actually
     * applied to the winner, so it reads as an ordinary change and gets the `change`
     * mode. `merged_into` is the loser's last entry and REPLACES its `soft_deleted`
     * event, which is why it is danger-toned: from this record's point of view it is
     * the moment it stopped being written to.
     */
    case 'merged_from':
      return { icon: 'link', tone: 'neutral', diffMode: 'change' };
    case 'merged_into':
      return { icon: 'link', tone: 'danger', diffMode: 'change' };
    case 'unmerged':
      return { icon: 'arrow-up', tone: 'warning', diffMode: 'change' };
    /** A RESYNC SIGNAL, not a diff (W5): its field ids say what to re-read. */
    case 'merge_repointed':
      return { icon: 'link', tone: 'neutral', diffMode: 'change' };
    /** THE ELEVEN ANNOTATION ACTIONS (notes-tasks-comments.md 3.2). */
    case 'note_added':
      return { icon: 'info', tone: 'neutral', diffMode: 'change' };
    case 'note_edited':
      return { icon: 'pencil', tone: 'neutral', diffMode: 'change' };
    case 'note_deleted':
      return { icon: 'trash', tone: 'danger', diffMode: 'change' };
    case 'task_added':
      return { icon: 'plus', tone: 'neutral', diffMode: 'change' };
    case 'task_edited':
      return { icon: 'pencil', tone: 'neutral', diffMode: 'change' };
    /**
     * Completion and its reversal are their own entries because they are their own
     * ACTIONS: a reader scanning a timeline is looking for the moment work was finished,
     * and a `task_reopened` folded into a change would hide the reversal that makes
     * the completion untrue.
     */
    case 'task_completed':
      return { icon: 'success', tone: 'neutral', diffMode: 'change' };
    case 'task_reopened':
      return { icon: 'arrow-up', tone: 'warning', diffMode: 'change' };
    case 'task_deleted':
      return { icon: 'trash', tone: 'danger', diffMode: 'change' };
    case 'comment_added':
      return { icon: 'mail', tone: 'neutral', diffMode: 'change' };
    case 'comment_edited':
      return { icon: 'pencil', tone: 'neutral', diffMode: 'change' };
    case 'comment_deleted':
      return { icon: 'trash', tone: 'danger', diffMode: 'change' };
    default:
      return assertNever(type, 'StreamRow.presentationOf');
  }
}

/**
 * What an annotation event should SHOW, given only the diff it carries.
 *
 * `after` for a write, `before` for a delete - the same rule 3.3 states for the payload,
 * read back out of it. A task event shows its title; a note or comment shows its body.
 * Null when there is nothing to show, including after an erasure scrubbed the values.
 */
function annotationBody(event: RecordEvent): string | null {
  const annotation = event.annotation;
  if (annotation === undefined) return null;
  const diff = annotation.body ?? annotation.title;
  if (diff === undefined) return null;
  const value = diff.after ?? diff.before;
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * A FILES CHANGE SAYS ITS OWN SENTENCE (`file-attachments.md` D14 section 12): "Added
 * shot.png", "Added two files and removed one". The event is an ordinary `updated` with two
 * jsonb arrays in its diff, so "Files changed to ..." would put a live file list in a title
 * line; `fileDiffSentence` is the reading D14 already wrote for it, and with the sentence in
 * the title there is nothing left for the row to fold.
 */
function filesSentence(event: RecordEvent, fields: readonly FieldMetadata[]): string | null {
  if (event.diff.length !== 1) return null;
  const only = event.diff[0];
  if (only === undefined) return null;
  const field = fields.find((entry) => entry.id === only.fieldId);
  if (field === undefined || fieldShape(field.type) !== 'files') return null;
  return fileDiffSentence(only.before, only.after);
}

/**
 * THE FIRST LINE, because a title line is one line. A call summary is paragraphs and the
 * stream is a list of rows: the whole body belongs on the Notes filter, where a note has a
 * box of its own, and the row's job is to say which note this is.
 */
function firstLine(text: string): string {
  const line = text.split('\n')[0]?.trim() ?? '';
  return line === '' ? text.trim() : line;
}

/** `"…"` around a name somebody typed, the way Apple's own alerts quote an item's name. */
function quoted(text: string): string {
  return `"${firstLine(text)}"`;
}

/**
 * ONE SENTENCE PER EVENT, and the whole vocabulary is here rather than one word per case,
 * because "plain" is a property of the sentence and not of the label inside it.
 *
 * A change says the field and the value it holds now, which is the one case where the value
 * cannot be a string: a MULTI_SELECT would stringify to "fitness,lifestyle" and a CURRENCY
 * would lose its code, so it is rendered by the same field component the record itself uses
 * (`ReadOnlyFieldValue`). Everything else is words.
 */
function titleOf(event: RecordEvent, fields: readonly FieldMetadata[]): ReactNode {
  const label = (fieldId: string): string =>
    fields.find((field) => field.id === fieldId)?.label ?? 'A field';
  const body = annotationBody(event);
  const count = event.diff.length;
  const first = event.diff[0];

  switch (event.type) {
    case 'created':
      /**
       * "Created from CSV import", and "Created" when a person did it: the source is worth a
       * title only where it is not a person, and a row that said "Created" over a secondary
       * line that said "CSV import" was one fact printed twice.
       */
      return event.actor.source === 'MANUAL'
        ? 'Created'
        : `Created from ${actorSentence(event.actor)}`;
    case 'updated':
    case 'merged_from': {
      const files = filesSentence(event, fields);
      if (files !== null) return files;
      if (count === 1 && first !== undefined) {
        const changed = first.after;
        if (changed === null || changed === undefined || changed === '') {
          return `${label(first.fieldId)} was cleared`;
        }
        const field = fields.find((entry) => entry.id === first.fieldId);
        if (field === undefined) return `${label(first.fieldId)} changed`;
        return (
          <>
            {`${field.label} changed to `}
            <ReadOnlyFieldValue
              objectId={event.objectId}
              recordId={event.recordId}
              field={field}
              value={changed}
            />
          </>
        );
      }
      return count === 0 ? 'Changed' : `${String(count)} fields changed`;
    }
    case 'deleted':
      return 'Archived';
    case 'restored':
      return 'Restored';
    case 'erased':
      return 'Values erased';
    case 'lock_released':
      return count === 1 && first !== undefined
        ? `${label(first.fieldId)} handed back to the automations`
        : 'Fields handed back to the automations';
    case 'write_blocked':
      return count === 1 && first !== undefined
        ? `A change to ${label(first.fieldId)} was refused`
        : `${String(count)} changes were refused`;
    case 'pending_approval':
      return count === 1 && first !== undefined
        ? `A change to ${label(first.fieldId)} is waiting for approval`
        : `${String(count)} changes are waiting for approval`;
    case 'proposal_expired':
      return count === 1 && first !== undefined
        ? `A proposed change to ${label(first.fieldId)} expired`
        : `${String(count)} proposed changes expired`;
    case 'merged_into':
      return 'Merged into another record';
    case 'unmerged':
      return 'The merge was undone';
    case 'merge_repointed':
      return 'Relinked by a merge';
    /** The things somebody wrote say themselves; the glyph says which kind they are. */
    case 'note_added':
    case 'note_edited':
      return body ?? 'A note';
    case 'comment_added':
    case 'comment_edited':
      return body ?? 'A comment';
    case 'note_deleted':
      return 'A note was deleted';
    case 'comment_deleted':
      return 'A comment was deleted';
    case 'task_added':
      return body === null ? 'A task was added' : `Added the task ${quoted(body)}`;
    case 'task_edited':
      return body === null ? 'A task was changed' : `Changed the task ${quoted(body)}`;
    case 'task_completed':
      return body === null ? 'A task was finished' : `Finished ${quoted(body)}`;
    case 'task_reopened':
      return body === null ? 'A task was reopened' : `Reopened ${quoted(body)}`;
    case 'task_deleted':
      return 'A task was deleted';
    default:
      return assertNever(event.type, 'StreamRow.titleOf');
  }
}

/**
 * A ROW WITH A FIELD DIFF FOLDS IT (`spec/screens/record.md` section 2 item 5: "collapsed
 * field changes, '3 changes' rows that expand").
 *
 * The title says what happened - "Name changed to Larkwood Studio" - and that is what a
 * reader scanning the stream needs; what an AUDIT needs is the value it replaced, and a
 * before/after pair is a table rather than a sentence. So the pair stays, one press away,
 * and at rest the row is one row. It is EVERY field diff and not only the long ones, because
 * a rule a reader has to guess at is not a rule: a chevron on a change row always means the
 * same thing.
 *
 * An ANNOTATION's diff is its own body (3.3 carries the whole note so no second request is
 * needed), and the title already says it, so those rows fold nothing.
 * hig: https://developer.apple.com/design/human-interface-guidelines/disclosure-controls
 */
function foldsItsDiff(event: RecordEvent, fields: readonly FieldMetadata[]): boolean {
  if (event.diff.length === 0 || isAnnotationAction(event.type)) return false;
  /* A files change already IS its sentence; there is no pair under it to reveal. */
  return filesSentence(event, fields) === null;
}

export interface StreamRowProps {
  readonly event: RecordEvent;
  readonly fields: readonly FieldMetadata[];
}

export function StreamRow({ event, fields }: StreamRowProps) {
  const presentation = presentationOf(event.type);
  const tone = TONES[presentation.tone];
  const folds = foldsItsDiff(event, fields);
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  const writer = actorParts(event.actor);
  /**
   * THE SECONDARY LINE IS THE WRITER AND, WHEN THERE IS ONE, THE NOTE THEY LEFT. Two spans
   * with a gap rather than a separator glyph: a middot between two facts is a character this
   * product would have to invent, and the kit's list row separates a secondary line's parts
   * with space.
   */
  const message = event.message;
  /** A `created` row already names its non-human writer in the title; it does not repeat it. */
  const namesWriterInTitle = event.type === 'created' && event.actor.source !== 'MANUAL';

  const title = <span className={cx('record-timeline-title', tone)}>{titleOf(event, fields)}</span>;

  return (
    /*
      P5: THE ROW APPLE WOULD BUILD IF IT SHIPPED A TIMELINE. It does not, so this is the
      two nearest constructions put together (`components/MAP.md`, "Timeline"): the
      notification row's information order - leading symbol, title, body, time - inside
      the kit's list-row geometry, with the glyph in the kit's own 24 slot and a hairline
      between rows instead of a divider element.
    */
    <li className="record-timeline-row">
      <span className={cx('record-timeline-glyph', tone)}>
        <Icon name={presentation.icon} size="0.9rem" />
      </span>
      <div className="record-timeline-body">
        {folds ? (
          /* THE WHOLE ROW IS THE DISCLOSURE'S TRIGGER, because the row is what a reader
             aims at, and the chevron is the mark rather than a second target beside it. */
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            className="record-timeline-trigger"
            onClick={() => setOpen((value) => !value)}
          >
            <Icon
              name="chevron-right"
              size="0.75rem"
              className={cx('shrink-0 text-ink-subtle', MOTION.turn, open && 'rotate-90')}
            />
            {title}
          </button>
        ) : (
          title
        )}
        <p className="record-timeline-secondary">
          {!namesWriterInTitle && <span>{writer.name}</span>}
          {/* INV-3: an agent write must never be mistakable for a human one, and in a row
              with no avatar the words are what carry that. The kind and the context are
              their own spans rather than parentheses inside the name, which is the same
              shape `ActorLabel` gives them where it has room to draw a symbol. */}
          {!namesWriterInTitle && writer.kind !== null && (
            <span className="record-timeline-kind">{writer.kind}</span>
          )}
          {!namesWriterInTitle && writer.context !== null && (
            <span className="truncate">{writer.context}</span>
          )}
          {message !== undefined && message !== '' && <span>{message}</span>}
        </p>
        {folds && (
          <div id={bodyId} hidden={!open}>
            <FieldDiffList
              objectId={event.objectId}
              recordId={event.recordId}
              fields={fields}
              diff={event.diff}
              mode={presentation.diffMode}
              className="mt-2"
            />
          </div>
        )}
      </div>
      {/* Relative time reads faster; the UTC instant is what an audit needs, so
          it stays one hover (and one screen-reader stop) away. */}
      <time dateTime={event.at} title={formatDateTime(event.at)} className="record-timeline-time">
        {formatRelative(event.at)}
      </time>
    </li>
  );
}
